import { useCallback, useEffect, useRef, useState } from 'react';
import { useResponse } from 'contexts/response';
import { Location } from 'types/location';
import {
  MigrationStep,
  MigrationStatusResponse,
  OPTIONAL_STEPS,
  STEP_ORDER
} from 'types/migration';
import {
  startMigration as apiStartMigration,
  startNewImport as apiStartNewImport,
  getMigrationStatus,
  uploadMigrationFile,
  restartMigration as apiRestartMigration,
  cancelMigration as apiCancelMigration,
  downloadTemplate,
  getLocationsWithMigrationHistory
} from 'services/migration';
import { getLocations } from 'services/locations';

const POLL_INTERVAL = 3000;
const POLL_IDLE_INTERVAL = 5000;

export function useMigration() {
  const { handleError, setSuccess } = useResponse();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState<MigrationStep>(MigrationStep.CUSTOMER);
  const [migrationStatus, setMigrationStatus] = useState<MigrationStatusResponse | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [polling, setPolling] = useState(false);
  const [processingFile, setProcessingFile] = useState<MigrationStep | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [locationIdsWithHistory, setLocationIdsWithHistory] = useState<Set<number>>(new Set());
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [skippingOptional, setSkippingOptional] = useState(false);
  const [retryingStep, setRetryingStep] = useState<MigrationStep | null>(null);
  const [skippedSteps, setSkippedSteps] = useState<Set<MigrationStep>>(new Set());
  const [drillDownStep, setDrillDownStep] = useState<MigrationStep | null>(null);

  const runIdRef = useRef<number | null>(null);

  const resetWizardState = useCallback(() => {
    setSelectedFile(null);
    setSelectedFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setProcessingFile(null);
    setSkippedSteps(new Set());
    setDrillDownStep(null);
    setRetryingStep(null);
    setShowRestartConfirm(false);
    setShowCancelConfirm(false);
    setShowSkipConfirm(false);
  }, []);

  useEffect(() => {
    getLocations().then(setLocations);
    getLocationsWithMigrationHistory()
      .then(ids => setLocationIdsWithHistory(new Set(ids)))
      .catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const urlLocationId = params.get('location');
    const savedId = urlLocationId || localStorage.getItem('migration_locationId');

    if (urlLocationId) {
      window.history.replaceState({}, '', '/settings/migration');
    }

    if (savedId) {
      getLocations().then(locs => {
        const loc = locs.find(l => l.id === Number(savedId));
        if (loc) {
          setSelectedLocation(loc);
          localStorage.setItem('migration_locationId', String(loc.id));
          apiStartMigration(loc.id).then(s => {
            runIdRef.current = s.migrationRunId ?? null;
            setMigrationStatus(s);
            if (s.overallStatus !== 'NOT_STARTED' && s.overallStatus !== 'ROLLED_BACK') {
              setActiveStep(s.currentStep);
            }
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!selectedLocation) return;
    const interval = setInterval(() => {
      getMigrationStatus(selectedLocation.id).then(s => {
        const expectedRunId = runIdRef.current;
        if (expectedRunId != null && s.migrationRunId != null && s.migrationRunId !== expectedRunId) {
          return;
        }
        runIdRef.current = s.migrationRunId ?? expectedRunId;
        setMigrationStatus(s);
        const inProgress = s.steps?.some(st => st.status === 'IN_PROGRESS');
        setPolling(!!inProgress);
        if (!inProgress) {
          const firstPending = s.steps?.findIndex(
            st => st.status === 'FAILED' || (st.status === 'PENDING' && !skippedSteps.has(st.step))
          );
          if (firstPending != null && firstPending > 0 && firstPending < STEP_ORDER.length) {
            const hasPartialBefore = s.steps?.slice(0, firstPending).some(
              st => st.status === 'PARTIALLY_COMPLETED'
            );
            if (!hasPartialBefore) {
              setActiveStep(STEP_ORDER[firstPending]);
            }
          }
          const noPendingRequired = s.steps?.every(
            st => st.status !== 'PENDING' || OPTIONAL_STEPS.includes(st.step)
          ) ?? true;
          if (s.steps?.some(st => st.status === 'FAILED') || s.steps?.some(st => st.status === 'CANCELLED') || s.steps?.some(st => st.status === 'PARTIALLY_COMPLETED') || noPendingRequired) {
            setProcessingFile(null);
          }
        }
      });
    }, polling ? POLL_INTERVAL : POLL_IDLE_INTERVAL);
    return () => clearInterval(interval);
  }, [selectedLocation, polling]);

  const navigateToStep = useCallback((step: MigrationStep) => {
    setActiveStep(step);
    setSkippedSteps(prev => {
      if (!prev.has(step)) return prev;
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
  }, []);

  const handleSelectLocation = useCallback(async (loc: Location) => {
    setSelectedLocation(loc);
    localStorage.setItem('migration_locationId', String(loc.id));
    try {
      const s = await apiStartMigration(loc.id);
      runIdRef.current = s.migrationRunId ?? null;
      setMigrationStatus(s);
      const resumeAt =
        s.overallStatus !== 'NOT_STARTED' && s.overallStatus !== 'ROLLED_BACK'
          ? s.currentStep ?? MigrationStep.CUSTOMER
          : MigrationStep.CUSTOMER;
      setActiveStep(resumeAt);
    } catch (e: any) {
      handleError(e?.message || 'Failed to start migration for this location');
    }
  }, [handleError]);

  const handleClearAndStartFresh = useCallback(async (loc: Location) => {
    setSelectedLocation(loc);
    localStorage.setItem('migration_locationId', String(loc.id));
    setRestarting(true);
    try {
      const s = await apiStartNewImport(loc.id);
      runIdRef.current = s.migrationRunId ?? null;
      resetWizardState();
      setMigrationStatus(s);
      setActiveStep(MigrationStep.CUSTOMER);
      setSuccess('Ready for a new import. Previously imported data is preserved.');
    } catch (e: any) {
      handleError(e?.message || 'Failed to start a new import');
    } finally {
      setRestarting(false);
    }
  }, [handleError, setSuccess, resetWizardState]);

  const handleChangeLocation = useCallback(() => {
    setSelectedLocation(null);
    setMigrationStatus(null);
    setActiveStep(MigrationStep.CUSTOMER);
    setPolling(false);
    runIdRef.current = null;
    resetWizardState();
    setRestarting(false);
    setCancelling(false);
    getLocationsWithMigrationHistory()
      .then(ids => setLocationIdsWithHistory(new Set(ids)))
      .catch(() => {});
  }, [resetWizardState]);

  const handleUpload = useCallback(async () => {
    if (!selectedLocation || !selectedFile || !migrationStatus?.migrationRunId) return;
    setProcessingFile(activeStep);
    const file = selectedFile;
    setSelectedFile(null);
    setSelectedFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      const result = await uploadMigrationFile(selectedLocation.id, activeStep, file);
      runIdRef.current = result.migrationRunId ?? runIdRef.current;
      setMigrationStatus(result);
      setPolling(true);
    } catch (e: any) {
      handleError(e?.message || 'Upload failed');
      setProcessingFile(null);
    }
  }, [selectedLocation, selectedFile, activeStep, migrationStatus, handleError]);

  const handleRetryStep = useCallback(async (step: MigrationStep) => {
    if (!selectedLocation || !selectedFile || !migrationStatus?.migrationRunId) return;
    setRetryingStep(step);
    setActiveStep(step);
    setDrillDownStep(null);
    const file = selectedFile;
    setSelectedFile(null);
    setSelectedFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    try {
      const result = await uploadMigrationFile(selectedLocation.id, step, file);
      runIdRef.current = result.migrationRunId ?? runIdRef.current;
      setMigrationStatus(result);
      setPolling(true);
    } catch (e: any) {
      handleError(e?.message || 'Upload failed');
    } finally {
      setRetryingStep(null);
    }
  }, [selectedLocation, selectedFile, migrationStatus, handleError]);

  const reconcileAfterFailure = useCallback(async (fallbackMessage: string, successStatus: string) => {
    if (!selectedLocation) {
      handleError(fallbackMessage);
      return;
    }
    try {
      const status = await getMigrationStatus(selectedLocation.id);
      runIdRef.current = status.migrationRunId ?? null;
      setMigrationStatus(status);
      if ((status.overallStatus as string) === successStatus) {
        setSuccess('The server completed the operation. Migration state has been refreshed.');
        return;
      }
      handleError(fallbackMessage);
    } catch {
      handleError(fallbackMessage);
    }
  }, [selectedLocation, handleError, setSuccess]);

  const handleRestart = useCallback(async () => {
    if (!selectedLocation) return;
    const runId = migrationStatus?.migrationRunId;
    if (!runId) {
      handleError('No migration run to reset');
      return;
    }
    setRestarting(true);
    try {
      await apiRestartMigration(selectedLocation.id, runId);
      const s = await apiStartMigration(selectedLocation.id);
      runIdRef.current = s.migrationRunId ?? null;
      setMigrationStatus(s);
      setActiveStep(MigrationStep.CUSTOMER);
      setShowRestartConfirm(false);
      setProcessingFile(null);
      setSuccess('Migration reset and a new import is ready');
    } catch (e: any) {
      await reconcileAfterFailure(e?.message || 'Restart failed; check the migration status', 'ROLLED_BACK');
    } finally {
      setRestarting(false);
    }
  }, [selectedLocation, migrationStatus, handleError, setSuccess, reconcileAfterFailure]);

  const handleCancel = useCallback(async () => {
    if (!selectedLocation) return;
    const runId = migrationStatus?.migrationRunId;
    if (!runId) {
      handleError('No migration run to cancel');
      return;
    }
    setCancelling(true);
    try {
      const s = await apiCancelMigration(selectedLocation.id, runId);
      runIdRef.current = s.migrationRunId ?? null;
      setMigrationStatus(s);
      setActiveStep(MigrationStep.CUSTOMER);
      setShowCancelConfirm(false);
      setProcessingFile(null);
      setSuccess('Migration cancelled successfully');
    } catch (e: any) {
      await reconcileAfterFailure(e?.message || 'Cancel failed; check the migration status', 'CANCELLED');
    } finally {
      setCancelling(false);
    }
  }, [selectedLocation, migrationStatus, handleError, setSuccess, reconcileAfterFailure]);

  const pickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    setSelectedFileName(file?.name || null);
  }, []);

  const currentStepResult = migrationStatus?.steps?.find(s => s.step === activeStep);
  const stepResultStatus = currentStepResult?.status;
  const isStepCompleted = stepResultStatus === 'COMPLETED';
  const isStepFailed = stepResultStatus === 'FAILED';
  const isStepPartial = stepResultStatus === 'PARTIALLY_COMPLETED';
  const isStepInProgress = (currentStepResult?.status === 'IN_PROGRESS' || processingFile === activeStep) && !isStepPartial;
  const isOptionalStep = OPTIONAL_STEPS.includes(activeStep);
  const currentStepIndex = migrationStatus?.steps?.findIndex(s => s.step === activeStep) ?? -1;

  const handleSkip = useCallback(async () => {
    if (currentStepIndex < 0) return;
    setSkipping(true);
    try {
      const nextIndex = currentStepIndex + 1;
      if (nextIndex < STEP_ORDER.length) {
        setActiveStep(STEP_ORDER[nextIndex]);
      }
      setShowSkipConfirm(false);
      setSkipping(false);
    } catch (e: any) {
      handleError(e?.message || 'Skip failed');
      setSkipping(false);
    }
  }, [currentStepIndex, handleError]);

  const handleSkipOptionalStep = useCallback(() => {
    if (currentStepIndex < 0) return;
    setSkippingOptional(true);
    setSkippedSteps(prev => new Set(prev).add(activeStep));
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEP_ORDER.length) {
      setActiveStep(STEP_ORDER[nextIndex]);
    }
    setSkippingOptional(false);
  }, [currentStepIndex, activeStep]);

  const allStepsCompleted = migrationStatus?.steps?.every(
    s => s.status === 'COMPLETED' || s.status === 'PARTIALLY_COMPLETED'
      || (s.status === 'PENDING' && OPTIONAL_STEPS.includes(s.step))
  ) ?? false;

  const handleStartNewImport = useCallback(async () => {
    if (!selectedLocation) return;
    const workerActive =
      processingFile != null ||
      (migrationStatus?.steps?.some(step => step.status === 'IN_PROGRESS') ?? false);
    if (workerActive) {
      handleError('An import is still running. Wait for it to stop before starting another import.');
      return;
    }
    setRestarting(true);
    try {
      const s = await apiStartNewImport(selectedLocation.id);
      runIdRef.current = s.migrationRunId ?? null;
      resetWizardState();
      setMigrationStatus(s);
      setActiveStep(MigrationStep.CUSTOMER);
      setSuccess('Ready for a new import. Previously imported data is preserved.');
    } catch (e: any) {
      handleError(e?.message || 'Failed to start new import');
    } finally {
      setRestarting(false);
    }
  }, [selectedLocation, processingFile, migrationStatus, handleError, setSuccess, resetWizardState]);

  const overallProgress = migrationStatus?.steps
    ? Math.round((migrationStatus.steps.filter(s => s.status === 'COMPLETED' || s.status === 'FAILED' || s.status === 'CANCELLED' || s.status === 'PARTIALLY_COMPLETED' || (s.status === 'PENDING' && OPTIONAL_STEPS.includes(s.step))).length / STEP_ORDER.length) * 100)
    : 0;

  return {
    activeStep, setActiveStep,
    migrationStatus,
    selectedLocation,
    locations,
    polling,
    processingFile,
    restarting,
    showRestartConfirm, setShowRestartConfirm,
    cancelling,
    showCancelConfirm, setShowCancelConfirm,
    selectedFileName,
    selectedFile,
    currentStepResult, stepResultStatus,
    isStepCompleted, isStepFailed, isStepPartial, isStepInProgress, isOptionalStep,
    currentStepIndex, overallProgress,
    allStepsCompleted,
    drillDownStep, setDrillDownStep,
    showSkipConfirm, setShowSkipConfirm,
    skipping,
    skippingOptional,
    retryingStep,
    locationIdsWithHistory,
    handleSkip,
    handleSkipOptionalStep,
    handleStartNewImport,
    handleRetryStep,
    fileInputRef,
    navigateToStep,
    handleSelectLocation,
    handleClearAndStartFresh,
    handleChangeLocation,
    handleUpload,
    handleRestart,
    handleCancel,
    handleFileChange,
    pickFile,
    downloadTemplate: (step?: MigrationStep) => downloadTemplate(typeof step === 'string' ? step : activeStep)
  };
}
